import type { Ficha as FichaDoContrato } from "../api"
import { Column } from "../componentes/TablePrincipal2"
import { SeloDoValor } from "../componentes/Selo"
import { data, dataHora, moeda, rotulo } from "../formato"
import { MAIUSCULA } from "../lib/classes"
import { BlocoEditavel } from "./Campos"
import { COLUNA_NUMERICA, DetalheDaFicha, TabelaDaFicha, VazioDaFicha, useLinhasAbertas } from "./Tabela"

type Medicao = FichaDoContrato["medicoes"][number]
type Empenho = FichaDoContrato["empenhos"][number]
type Pagamento = FichaDoContrato["pagamentos"][number]

const NUMERO = "tabular-nums"
const VALOR = "block text-center tabular-nums"

export function BlocoMedicoes({ ficha }: { ficha: FichaDoContrato }) {
  const linhas = useLinhasAbertas<number>()
  return (
    <BlocoEditavel
      titulo="Medições"
      editavel={false}
      motivoSemEdicao="a medição se atesta em Minha fiscalização"
      recado={
        "O que foi entregue, competência a competência. Atestar é ato de quem " +
        "está designado, e acontece na tela Minha fiscalização — não aqui."
      }
      leitura={
        <TabelaDaFicha<Medicao>
          dados={ficha.medicoes}
          ordenar={{ numero: (m) => m.numero, competencia_inicio: (m) => m.competencia_inicio, valor: (m) => Number(m.valor) }}
          vazio={<VazioDaFicha titulo="Nenhuma medição registrada" />}
          linhaClicavel={(m) => m.ressalva !== null}
          aoClicarNaLinha={(m) => linhas.alternar(m.id)}
          linhaAberta={(m) => linhas.aberta(m.id)}
          detalheDaLinha={(m) => <DetalheDaFicha rotulo="Ressalva">{m.ressalva}</DetalheDaFicha>}
        >
          <Column<Medicao> name="numero" label="Medição" render={(_v, m) => <span className={NUMERO}>{m.numero}</span>} />
          <Column<Medicao>
            name="competencia_inicio"
            label="Competência"
            render={(_v, m) => <span className={NUMERO}>{`${data(m.competencia_inicio)} a ${data(m.competencia_fim)}`}</span>}
          />
          <Column<Medicao> name="valor" label="Valor" classe={COLUNA_NUMERICA} render={(_v, m) => <span className={VALOR}>{moeda(m.valor)}</span>} />
          <Column<Medicao> name="situacao" label="Situação" render={(_v, m) => <SeloDoValor valor={m.situacao} rotulo={rotulo(m.situacao)} />} />
        </TabelaDaFicha>
      }
    />
  )
}

export function BlocoAtestes({ ficha }: { ficha: FichaDoContrato }) {
  const linhas = useLinhasAbertas<number>()
  const atestadas = ficha.medicoes.filter((m) => m.atestada_em !== null)
  return (
    <BlocoEditavel
      titulo="Atestes"
      editavel={false}
      motivoSemEdicao="o ateste é ato de quem está designado"
      recado="O ateste libera o pagamento do fornecedor e fica registrado com data e hora."
      leitura={
        <TabelaDaFicha<Medicao>
          dados={atestadas}
          ordenar={{ atestada_em: (m) => m.atestada_em, valor: (m) => Number(m.valor) }}
          vazio={
            <VazioDaFicha
              titulo="Nenhuma medição atestada ainda"
              descricao={
                ficha.medicoes.length > 0
                  ? "Há medição aguardando: quem estiver designado atesta em Minha fiscalização."
                  : "Não há medição registrada neste contrato."
              }
            />
          }
          linhaClicavel={(m) => m.ressalva !== null}
          aoClicarNaLinha={(m) => linhas.alternar(m.id)}
          linhaAberta={(m) => linhas.aberta(m.id)}
          detalheDaLinha={(m) => <DetalheDaFicha rotulo="Ressalva">{m.ressalva}</DetalheDaFicha>}
        >
          <Column<Medicao> name="numero" label="Medição" render={(_v, m) => <span className={NUMERO}>{m.numero}</span>} />
          <Column<Medicao> name="atestada_em" label="Atestada em" render={(_v, m) => <span className={NUMERO}>{dataHora(m.atestada_em)}</span>} />
          <Column<Medicao> name="valor" label="Valor" classe={COLUNA_NUMERICA} render={(_v, m) => <span className={VALOR}>{moeda(m.valor)}</span>} />
          <Column<Medicao> name="ressalva" label="Ressalva" render={(_v, m) => (m.ressalva === null ? "Sem ressalva" : "Com ressalva")} />
        </TabelaDaFicha>
      }
    />
  )
}

export function BlocoEmpenhos({ ficha }: { ficha: FichaDoContrato }) {
  return (
    <BlocoEditavel
      titulo="Empenhos"
      editavel={false}
      recado="A reserva de recurso que amarra o pagamento à dotação."
      leitura={
        <TabelaDaFicha<Empenho>
          dados={ficha.empenhos}
          ordenar={{ data: (e) => e.data, valor: (e) => Number(e.valor) }}
          vazio={<VazioDaFicha titulo="Nenhum empenho registrado" />}
        >
          <Column<Empenho> name="numero" label="Empenho" render={(_v, e) => <span className={NUMERO}>{e.numero}</span>} />
          <Column<Empenho> name="data" label="Data" render={(_v, e) => <span className={NUMERO}>{data(e.data)}</span>} />
          <Column<Empenho> name="exercicio" label="Exercício" render={(_v, e) => <span className={NUMERO}>{e.exercicio}</span>} />
          <Column<Empenho> name="tipo" label="Tipo" render={(_v, e) => <span className={MAIUSCULA}>{rotulo(e.tipo)}</span>} />
          <Column<Empenho> name="valor" label="Valor" classe={COLUNA_NUMERICA} render={(_v, e) => <span className={VALOR}>{moeda(e.valor)}</span>} />
          <Column<Empenho> name="valor_anulado" label="Anulado" classe={COLUNA_NUMERICA} render={(_v, e) => <span className={VALOR}>{moeda(e.valor_anulado)}</span>} />
        </TabelaDaFicha>
      }
    />
  )
}

export function BlocoPagamentos({ ficha }: { ficha: FichaDoContrato }) {
  return (
    <BlocoEditavel
      titulo="Pagamentos"
      editavel={false}
      recado="O que saiu do caixa, com a retenção separada do bruto."
      leitura={
        <TabelaDaFicha<Pagamento>
          dados={ficha.pagamentos}
          ordenar={{ data_pagamento: (p) => p.data_pagamento, valor_liquido: (p) => Number(p.valor_liquido) }}
          vazio={<VazioDaFicha titulo="Nenhum pagamento registrado" />}
        >
          <Column<Pagamento> name="documento_fiscal" label="Documento" render={(_v, p) => p.documento_fiscal ?? "—"} />
          <Column<Pagamento> name="data_pagamento" label="Pagamento" render={(_v, p) => <span className={NUMERO}>{data(p.data_pagamento)}</span>} />
          <Column<Pagamento> name="valor_bruto" label="Bruto" classe={COLUNA_NUMERICA} render={(_v, p) => <span className={VALOR}>{moeda(p.valor_bruto)}</span>} />
          <Column<Pagamento> name="valor_retencoes" label="Retenções" classe={COLUNA_NUMERICA} render={(_v, p) => <span className={VALOR}>{moeda(p.valor_retencoes)}</span>} />
          <Column<Pagamento> name="valor_liquido" label="Valor líquido" classe={COLUNA_NUMERICA} render={(_v, p) => <span className={VALOR}>{moeda(p.valor_liquido)}</span>} />
          <Column<Pagamento> name="situacao" label="Situação" render={(_v, p) => <SeloDoValor valor={p.situacao} rotulo={rotulo(p.situacao)} />} />
        </TabelaDaFicha>
      }
    />
  )
}
